import { CloseRounded, DeleteRounded, DirectionsWalkRounded, InfoRounded, OpenInNewRounded, PlayArrowRounded, SaveRounded, SpeedRounded, ViewComfyRounded, WarningAmberRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Alert, Autocomplete, Button, CircularProgress, Drawer, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Modal, Stack, TextField, Tooltip, Typography } from '@mui/material'
import { DataGrid, GridColDef, GridRenderEditCellParams } from '@mui/x-data-grid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { ApprovalStatus } from '../../definitions/Approval'
import { IContract } from '../../definitions/Contract'
import { IAncestorFlow } from '../../definitions/Nodes'
import { IFlowRunConfig } from '../../definitions/NodesExecution'
import { changeRoute } from '../../store/slices/SessionSlice'
import StartRunModal from '../flows/StartRunModal'
import useCommand from '../hooks/useCommand'
import useInvalidate from '../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import { snackbar } from '../services/SnackbarService'

interface P {
    contractUUID: string
    settlementRef: IContract['settlementRef']
    approvalStatus: ApprovalStatus
    refetch: () => void
}

const EXCLUDED_KEYS = ['contractId', 'userId', 'periodStart', 'periodEnd', 'type', 'settlementType']

const NO_BORDER_TEXTFIELD = {
    '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: 'transparent' },
        '&:hover fieldset': { borderColor: 'transparent' },
        '&.Mui-focused fieldset': { borderColor: 'transparent' }
    }
}

const extractGlobals = (rows: { [key: string]: any }) => Object.fromEntries(rows.map(r => [r.key, r.value]))

const updateRowValue = (rows: { [key: string]: any }, key: string, value: any) => rows.map(r => (r.key === key ? { ...r, value, warn: false } : r))

const deleteRow = (rows: { [key: string]: any }, key: string) => rows.filter(r => r.key !== key)

const resolveAncestorAndChild = (response: IAncestorFlow[], settlementRef: IContract['settlementRef']) => {
    if (!response || !settlementRef?.uuid || !settlementRef?.ancestorKey) {
        return { ancestor: null, child: null }
    }

    const ancestor = response.find(a => a.ancestorKey === settlementRef.ancestorKey)
    if (!ancestor) return { ancestor: null, child: null }

    return {
        ancestor,
        child: ancestor.children.find(c => c.uuid === settlementRef.uuid)
    }
}

const computeGlobalsRows = (selectedChild: IAncestorFlow['children'][0], settlementRef: IContract['settlementRef']) => {
    if (!selectedChild) return []

    const templateGlobals = selectedChild.globals ?? {}
    const settlementGlobals = settlementRef?.globals ?? {}
    const merged = { ...templateGlobals, ...settlementGlobals }

    return Object.entries(merged)
        .filter(([key]) => !EXCLUDED_KEYS.includes(key))
        .map(([key, value]) => ({
            id: key,
            key,
            value,
            warn: !(key in templateGlobals)
        }))
}

const ValueEditCell = (params: GridRenderEditCellParams) => {
    const type = typeof params.row.value

    const handleChange = e => {
        const newValue = type === 'boolean'
            ? e.target.value === 'true'
            : type === 'number'
                ? Number(e.target.value)
                : e.target.value

        params.api.setEditCellValue({
            id: params.id,
            field: params.field,
            value: newValue
        })
    }

    return type === 'boolean'
        ? (
                <TextField
                    select
                    sx={NO_BORDER_TEXTFIELD}
                    size='small'
                    fullWidth
                    value={params.value ? 'true' : 'false'}
                    onChange={handleChange}>
                    <MenuItem value='true'>true</MenuItem>
                    <MenuItem value='false'>false</MenuItem>
                </TextField>
            )
        : (
                <TextField
                    sx={NO_BORDER_TEXTFIELD}
                    size='small'
                    fullWidth
                    type={type === 'number' ? 'number' : 'text'}
                    value={params.value ?? ''}
                    onChange={handleChange}
                />
            )
}

const SettlementTab = ({ contractUUID, settlementRef, approvalStatus, refetch }: P) => {
    const dispatch = useDispatch()

    const [openDrawer, setOpenDrawer] = useState(false)
    const [openStartRunModal, setOpenStartRunModal] = useState(false)
    const [globalsRows, setGlobalsRows] = useState([])
    const [selectedAncestor, setSelectedAncestor] = useState<IAncestorFlow>(null)
    const [selectedChild, setSelectedChild] = useState<IAncestorFlow['children'][0]>(null)
    const [runMode, setRunMode] = useState<'full' | 'dry-run'>(null)
    const [engineConfig, setEngineConfig] = useState<IFlowRunConfig>(null)
    const [noAnim, setNoAnim] = useState(false)
    const [anchorElMenu, setAnchorElMenu] = useState(null)
    const [isPolling, setIsPolling] = useState(false)
    const invalidateSettlement = useInvalidate({ queryKey: buildQueryKey('settlements' + '/*') })

    // Fast Engine Start
    useEffect(() => {
        if (!engineConfig) return

        let cancelled = false
        let timeoutId: NodeJS.Timeout

        setIsPolling(true)
        snackbar.show({ severity: 'info', text: 'Execution started, wait until it completes!' })

        const poll = async (uuid: string) => {
            if (cancelled) return

            try {
                const env = await FlowApiClient.getFlowExecutionEnvironment(uuid)

                if (env?.status === 'completed') {
                    if (env?.mode === 'dry-run') {
                        dispatch(
                            changeRoute({
                                route: 'flow',
                                params: { eeId: env.uuid }
                            })
                        )
                    } else {
                        const settlement = await FlowApiClient.getSettlementByExecutionEnvironmentUUID(uuid)

                        await invalidateSettlement()

                        dispatch(
                            changeRoute({
                                route: 'settlement-details',
                                params: {
                                    settlementId: settlement.uuid,
                                    contractId: settlement.contractId,
                                    eeId: settlement.executionEnvironmentId
                                }
                            })
                        )
                    }
                    setIsPolling(false)
                    return
                }

                if (env?.status === 'failed') {
                    snackbar.show({ severity: 'error', text: 'Something went wrong during the execution of engine' })
                    setIsPolling(false)
                    cancelled = true
                    return
                }

                timeoutId = setTimeout(() => poll(uuid), 3000)
            } catch (err) {
                console.error(err)

                snackbar.show({
                    severity: 'error',
                    text: 'Something went wrong during the execution of engine: ' + err
                })

                setIsPolling(false)
                cancelled = true
            }
        }

        const start = async () => {
            const response = engineConfig?.dryRunConfig?.samplePercentage
                ? await FlowApiClient.startDryRun(engineConfig)
                : await FlowApiClient.startFlow(engineConfig)

            FlowApiClient.executeFlow(response.uuid)
            poll(response.uuid)
        }

        start()

        return () => {
            cancelled = true
            clearTimeout(timeoutId)
            setEngineConfig(null)
            setIsPolling(false)
        }
    }, [engineConfig])

    // QUERY: TEMPLATE NAMES
    const { status, response } = useQuery({
        queryKey: buildQueryKey('getApprovedFlowTemplateNames'),
        queryFn: () => FlowApiClient.getApprovedFlowTemplateNames(),
        cache: false
    })

    // SYNC ANCESTOR & CHILD
    useEffect(() => {
        if (!openDrawer || !response) return

        const { ancestor, child } = resolveAncestorAndChild(response, settlementRef)
        setSelectedAncestor(ancestor)
        setSelectedChild(child)
    }, [openDrawer, response, settlementRef])

    // SYNC GLOBALS
    useEffect(() => {
        setGlobalsRows(computeGlobalsRows(selectedChild, settlementRef))
    }, [selectedChild, settlementRef])

    // DISPATCH TO ENGINE
    const handleDispatchToSettlementEngine = useCallback(() => {
        if (selectedChild) {
            if (selectedChild.uuid === 'latest') {
                const head = selectedAncestor.children.find(v => v.uuid !== 'latest' && v.isHead === true)
                if (head)
                    dispatch(changeRoute({ route: 'flow', params: { flowId: head.uuid } }))
                else {
                    const latest = selectedAncestor.children.filter(c => c.uuid !== 'latest').reduce((max, c) => c.version > max.version ? c : max)
                    dispatch(changeRoute({ route: 'flow', params: { flowId: latest.uuid } }))
                }
            } else
                dispatch(changeRoute({ route: 'flow', params: { flowId: selectedChild.uuid } }))
        }
    }, [selectedChild, selectedAncestor])

    // UPDATE SETTLEMENT REF
    const { execute: updateSettlementRef, status: statusUpdateSettlementRef } = useCommand({
        commandFn: async (data: { contractUUID: string, settlementRef: IContract['settlementRef'] }) => await FlowApiClient.updateSettlementEngineRef(data.contractUUID, data.settlementRef),
        onFinish: result => {
            if (result.status === 'success' && result.response) {
                refetch()
                setOpenDrawer(false)
                snackbar.show({ severity: 'success', text: `Settlement engine reference updated` })
            } else {
                snackbar.show({ severity: 'error', text: 'Something went wrong during update.' })
            }
        }
    })

    const ancestorOptions = useMemo(() => response ?? [], [response])

    const versionOptions = useMemo(() => {
        if (!selectedAncestor) return []
        return selectedAncestor.children
            .map(c => ({ ...c }))
    }, [selectedAncestor])

    // TEMPLATE & FLOW
    const selectedEngine = useMemo(() => {
        if (!selectedChild) return null
        return {
            uuid: selectedChild.uuid,
            ancestorKey: selectedAncestor.ancestorKey,
            anctorName: selectedAncestor.ancestorName,
            name: selectedChild.name,
            globals: extractGlobals(globalsRows),
            version: selectedChild.version
        }
    }, [selectedChild, selectedAncestor, globalsRows])

    const flow = useMemo(() => {
        if (selectedEngine?.uuid === 'latest') {
            const head = selectedAncestor.children.find(c => c.uuid !== 'latest' && c.isHead)
            if (head) {
                return {
                    uuid: head?.uuid,
                    versioning: {
                        ancestorKey: selectedAncestor.ancestorKey
                    },
                    globals: {
                        ...selectedEngine.globals,
                        contractId: settlementRef?.globals?.contractId,
                        periodStart: settlementRef?.globals?.periodStart,
                        periodEnd: settlementRef?.globals?.periodEnd
                    }
                }
            } else {
                const latest = selectedAncestor.children.filter(c => c.uuid !== 'latest').reduce((max, c) => c.version > max.version ? c : max)
                return {
                    uuid: latest?.uuid,
                    versioning: {
                        ancestorKey: selectedAncestor.ancestorKey
                    },
                    globals: {
                        ...selectedEngine.globals,
                        contractId: settlementRef?.globals?.contractId,
                        periodStart: settlementRef?.globals?.periodStart,
                        periodEnd: settlementRef?.globals?.periodEnd
                    }
                }
            }
        } else {
            return {
                uuid: selectedEngine?.uuid ?? settlementRef?.uuid,
                versioning: {
                    ancestorKey: selectedAncestor?.ancestorKey ?? settlementRef?.ancestorKey
                },
                globals: {
                    ...(selectedEngine?.globals ?? settlementRef?.globals ?? {}),
                    contractId: settlementRef?.globals?.contractId,
                    periodStart: settlementRef?.globals?.periodStart,
                    periodEnd: settlementRef?.globals?.periodEnd
                }
            }
        }
    }, [selectedEngine, settlementRef])

    // DATAGRID COLUMNS
    const columns: GridColDef[] = useMemo(() => [
        { field: 'key', headerName: 'Key', flex: 1, sortable: false },
        {
            field: 'value',
            headerName: 'Value',
            flex: 1.2,
            editable: true,
            renderEditCell: params => <ValueEditCell {...params} />
        },
        {
            field: 'action',
            headerName: '',
            width: 80,
            sortable: false,
            renderCell: params => {
                const { warn, key } = params.row
                if (!warn) return null

                return (
                    <Stack direction='row' alignItems='center' height='100%' gap={1}>
                        <Tooltip title='This key exists in settlement but not in the selected engine.'>
                            <WarningAmberRounded color='warning' fontSize='small' />
                        </Tooltip>
                        <IconButton
                            size='small'
                            onClick={() => setGlobalsRows(prev => deleteRow(prev, key))}
                            color='error'>
                            <DeleteRounded fontSize='small' />
                        </IconButton>
                    </Stack>
                )
            }
        }
    ], [])

    const loading = status === 'loading' || status === 'stale'
    const loadingSave = statusUpdateSettlementRef === 'loading'

    return (
        <>
            {/* TOP ACTIONS */}
            <Stack flexDirection='row' alignItems='center' justifyContent='flex-end' gap={2}>
                <Button
                    disabled={isPolling}
                    variant='text'
                    startIcon={<ViewComfyRounded color={isPolling ? 'disabled' : 'primary'} />}
                    onClick={() => setOpenDrawer(true)}>
                    {settlementRef ? 'Change Engine' : 'Choose Engine'}
                </Button>

                <LoadingButton
                    loading={isPolling && runMode === 'dry-run'}
                    loadingPosition='start'
                    variant='contained'
                    startIcon={<PlayArrowRounded />}
                    color='dryRun'
                    disabled={!settlementRef?.uuid || !approvalStatus || (runMode === 'full' && isPolling)}
                    onClick={e => {
                        setRunMode('dry-run')
                        setAnchorElMenu(e.currentTarget)
                    }}>
                    Dry Run
                </LoadingButton>

                <LoadingButton
                    loading={isPolling && runMode === 'full'}
                    loadingPosition='start'
                    variant='contained'
                    startIcon={<PlayArrowRounded />}
                    color='primary'
                    disabled={!settlementRef?.uuid || !approvalStatus || approvalStatus != 'approved' || (runMode === 'dry-run' && isPolling)}
                    onClick={e => {
                        setRunMode('full')
                        setAnchorElMenu(e.currentTarget)
                    }}>
                    Full Run
                </LoadingButton>

                <Tooltip
                    slotProps={{ tooltip: { sx: { padding: 1 } } }}
                    title={(
                        <Stack spacing={1}>
                            <Stack flexDirection='row' gap={0.5}>
                                <DirectionsWalkRounded fontSize='small' />
                                <Typography variant='inherit'>
                                    <b>Normal mode</b>
                                    :
                                    {' '}
                                    showing animations during the execution
                                </Typography>
                            </Stack>
                            <Stack flexDirection='row' gap={0.5}>
                                <SpeedRounded fontSize='small' />
                                <Typography variant='inherit'>
                                    <b>Fast mode</b>
                                    :
                                    {' '}
                                    avoid showing animations and remain in this page while running
                                </Typography>
                            </Stack>
                        </Stack>
                    )}
                    arrow>
                    <InfoRounded fontSize='inherit' color='disabled' />
                </Tooltip>

                {Boolean(anchorElMenu) && (
                    <Menu
                        anchorEl={anchorElMenu}
                        open={Boolean(anchorElMenu)}
                        onClose={() => setAnchorElMenu(null)}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left'
                        }}
                        slotProps={{ paper: { sx: { marginTop: 0.5 } } }}>
                        <MenuItem onClick={() => {
                            setNoAnim(false)
                            setAnchorElMenu(null)
                            setOpenStartRunModal(true)
                        }}>
                            <ListItemIcon>
                                <DirectionsWalkRounded fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>Normal mode</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => {
                            setNoAnim(true)
                            setAnchorElMenu(null)
                            setOpenStartRunModal(true)
                        }}>
                            <ListItemIcon>
                                <SpeedRounded fontSize='small' />
                            </ListItemIcon>
                            <ListItemText>Fast mode</ListItemText>
                        </MenuItem>
                    </Menu>
                )}
            </Stack>

            {/* DRAWER */}
            {openDrawer && (
                <Drawer
                    anchor='right'
                    open={openDrawer}
                    onClose={() => setOpenDrawer(false)}
                    PaperProps={{
                        elevation: 0,
                        sx: {
                            overflow: 'hidden',
                            padding: 0,
                            borderRadius: '12px 0px 0px 12px',
                            width: 460
                        }
                    }}>
                    <>
                        {/* HEADER */}
                        <Stack sx={{ backgroundColor: theme => theme.palette.background.default }}>
                            <Stack flexDirection='row' alignItems='center' justifyContent='space-between' sx={{ padding: 3 }}>
                                <Stack flexDirection='row' alignItems='center' gap={2}>
                                    <ViewComfyRounded fontSize='large' />
                                    <Typography variant='subtitle2' fontWeight={500}>
                                        {!settlementRef ? 'Choose an Engine' : 'Change an Engine'}
                                    </Typography>
                                </Stack>

                                <Stack flexDirection='row' alignItems='center' gap={2}>
                                    <IconButton
                                        onClick={() =>
                                            updateSettlementRef({
                                                contractUUID,
                                                settlementRef: {
                                                    uuid: selectedEngine.uuid === 'latest' ? 'latest' : selectedEngine.uuid,
                                                    ancestorKey: selectedEngine.ancestorKey,
                                                    globals: selectedEngine.globals,
                                                    version: selectedEngine.version,
                                                    ancestorName: selectedEngine.anctorName
                                                }
                                            })}
                                        disabled={!selectedEngine}>
                                        {loadingSave ? <CircularProgress size={18} /> : <SaveRounded />}
                                    </IconButton>

                                    <IconButton onClick={() => setOpenDrawer(false)}>
                                        <CloseRounded />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Stack>

                        {/* BODY */}
                        <Stack spacing={3} sx={{ padding: 3, overflow: 'hidden' }}>
                            {/* ANCESTOR */}
                            <Autocomplete
                                size='small'
                                fullWidth
                                loading={loading}
                                disabled={loadingSave}
                                options={ancestorOptions}
                                value={selectedAncestor}
                                getOptionLabel={o => o?.ancestorName ?? ''}
                                isOptionEqualToValue={(o, v) => o.ancestorKey === v.ancestorKey}
                                onChange={(_, newValue) => {
                                    setSelectedAncestor(newValue)
                                    setSelectedChild(null)
                                }}
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props} key={option.ancestorKey}>
                                            {option.ancestorName}
                                        </li>
                                    )
                                }}
                                renderInput={params => (
                                    <TextField
                                        {...params}
                                        label='Ancestor'
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loading && <CircularProgress color='inherit' size={18} />}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            )
                                        }}
                                    />
                                )}
                            />

                            {/* VERSION */}
                            <Stack flexDirection='row' alignItems='center' gap={1}>
                                <Autocomplete
                                    size='small'
                                    fullWidth
                                    disabled={!selectedAncestor || loadingSave}
                                    options={versionOptions}
                                    value={selectedChild}
                                    getOptionLabel={o => o.uuid === 'latest' ? 'latest' : `${o.name} (v${o.version} ${o.isHead ? '- approved' : ''})`}
                                    isOptionEqualToValue={(o, v) => o.uuid === v.uuid}
                                    onChange={(_, newValue) => setSelectedChild(newValue)}
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label='Version'
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {loading && <CircularProgress color='inherit' size={18} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                )
                                            }}
                                        />
                                    )}
                                />

                                <IconButton onClick={handleDispatchToSettlementEngine} disabled={!selectedEngine || loadingSave}>
                                    <OpenInNewRounded />
                                </IconButton>
                            </Stack>

                            {/* GLOBALS */}
                            <Typography variant='subtitle2' fontWeight={500}>
                                Globals List
                            </Typography>

                            {!selectedChild
                                ? (
                                        <Alert severity='info'>
                                            Select an engine version to review its global keys and values.
                                        </Alert>
                                    )
                                : (
                                        <DataGrid
                                            rows={globalsRows}
                                            columns={columns}
                                            disableColumnMenu
                                            disableRowSelectionOnClick
                                            hideFooter
                                            processRowUpdate={newRow => {
                                                setGlobalsRows(prev => updateRowValue(prev, newRow.key, newRow.value))
                                                return newRow
                                            }}
                                        />
                                    )}
                        </Stack>
                    </>
                </Drawer>
            )}

            {/* RUN MODAL */}
            {openStartRunModal && (
                <Modal
                    open={openStartRunModal}
                    onClose={() => setOpenStartRunModal(false)}
                    sx={{
                        display: 'flex',
                        width: 'calc(100vw - var(--left-nav-width))',
                        left: 'var(--left-nav-width)',
                        top: '8vh'
                    }}>
                    <div style={{ margin: 'auto auto' }}>
                        {runMode === 'full'
                            ? (
                                    <StartRunModal flow={flow} setOpenStartRunModal={setOpenStartRunModal} runMode='full' startImmediately setEngineConfig={setEngineConfig} noAnim={noAnim} />
                                )
                            : (
                                    <StartRunModal flow={flow} setOpenStartRunModal={setOpenStartRunModal} runMode='dry' startImmediately setEngineConfig={setEngineConfig} noAnim={noAnim} />
                                )}
                    </div>
                </Modal>
            )}
        </>
    )
}

export default SettlementTab
